/**
 * ADR-0185 Phase B — the pending-moves table (above the sheet): one row per
 * queued job, the How switchable inline across every strategy, the full
 * plain-language breakdown ALWAYS on the row (owner-ruled: nothing about a
 * move hides behind Edit), Edit… / Remove per row. Switching to a
 * parameterized strategy hands off to the popover for its picker.
 */
import { formatTimestamp } from "../../../../lib/formatTime.js";
import type { Placement, PlacementKind } from "../../boardTypes.js";
import { jobLabel } from "./laneMath.js";
import { STRATEGIES, moveBreakdown, type PlanJobs } from "./refineMath.js";

export function PendingMoves(props: {
  moves: readonly Placement[];
  planJobs: PlanJobs;
  busy: boolean;
  /** Who last queued moves on this model, and when — the working copy is shared per model. */
  queuedBy: { name: string | null; at: string | null };
  /** Replace one move's strategy in place (unparameterized strategies). */
  onSwitchHow: (move: Placement, kind: PlacementKind) => void;
  /** Open the popover on this move (parameterized strategies, and Edit…). */
  onEdit: (move: Placement, anchor: HTMLElement, kind?: PlacementKind) => void;
  onRemove: (move: Placement) => void;
}) {
  if (props.moves.length === 0) return null;
  return (
    <div className="card boardPendingCard">
      <div className="boardPendingHead">
        <strong>Pending moves — refine before replanning</strong>
        <span className="muted boardSheetHint">
          {props.queuedBy.at ? `queued${props.queuedBy.name ? ` by ${props.queuedBy.name}` : ""}, ${formatTimestamp(props.queuedBy.at)} · ` : ""}
          shared with everyone on this board · nothing changes until you hit Replan
        </span>
      </div>
      <div className="tableWrap">
        <table className="table boardPendingTable">
          <thead>
            <tr>
              <th>Job (part)</th>
              <th>How</th>
              <th>Machine</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {props.moves.map((m) => {
              const plan = props.planJobs.get(m.job);
              const machine = m.machine ?? plan?.machine ?? "—";
              return (
                <tr key={m.job}>
                  <td className="mono">{jobLabel({ job: m.job, part: plan?.part ?? null })}</td>
                  <td>
                    <select
                      className="select boardPendingHow"
                      value={m.kind}
                      disabled={props.busy}
                      onChange={(e) => {
                        const kind = e.target.value as PlacementKind;
                        const decl = STRATEGIES.find((s) => s.kind === kind);
                        if (decl?.parameterized) props.onEdit(m, e.currentTarget, kind);
                        else props.onSwitchHow(m, kind);
                      }}
                    >
                      {STRATEGIES.map((s) => (
                        <option key={s.kind} value={s.kind}>{s.label}</option>
                      ))}
                    </select>
                    <div className="muted boardPendingDetail">{moveBreakdown(m, props.planJobs, props.moves)}</div>
                  </td>
                  <td>
                    {machine}
                    {m.machine && plan && m.machine !== plan.machine ? <span className="muted"> (from {plan.machine})</span> : null}
                  </td>
                  <td className="boardPendingActions">
                    <button type="button" className="btn btnSm" disabled={props.busy} onClick={(e) => props.onEdit(m, e.currentTarget)}>
                      Edit…
                    </button>
                    <button type="button" className="btn btnSm" disabled={props.busy} onClick={() => props.onRemove(m)}>
                      Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
